// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkoutOrderSummaryStyle } from "@hardwoods/App/Widgets/Cart/OrderSummary.style";
import { orderSummaryTotalStyle, orderSummaryValueTextStyle } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { color } from "@hardwoods/XDL/Utilities";
import { displayFlexOnPrint } from "@hardwoods/XDL/Utilities/SharedStyles";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

// For MyAccounts Pages
interface OrderSummeryTableStyles {
    mainWrapper?: InjectableCss;
    titleContainer?: GridContainerProps;
    orderSummeryTitle?: TypographyProps;
    container?: GridContainerProps;
    itemLabel?: TypographyProps;
    itemValue?: TypographyProps;
    totalLabel?: InjectableCss;
    totalValue?: InjectableCss;
    subtotalLabel?: InjectableCss;
    subtotalValue?: InjectableCss;
    hrBorder?: InjectableCss;
    orderSummeryTitleGridItem?: GridItemProps;
    hrBorderGridItem?: GridItemProps;
    totalLabelGridItem?: GridItemProps;
    totalValueGridItem?: GridItemProps;
    itemLabelGridItem?: GridItemProps;
    itemValueGridItem?: GridItemProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    itemCountGridItem?: GridItemProps;
    itemCountText?: TypographyProps;
    itemTotalGridItem?: GridItemProps;
}

export const orderSummeryTableStyles: OrderSummeryTableStyles = {
    mainWrapper: {
        ...checkoutOrderSummaryStyle.container,
    },
    titleContainer: {
        gap: 0,
    },
    orderSummeryTitle: {
        ...checkoutOrderSummaryStyle.title,
    },
    container: {
        gap: 0,
        css: css`
            background-color: ${color("common.white")};
            padding: 20px;

            div[class^="GridItemStyle"]:nth-last-of-type(n + 3) {
                margin-bottom: 25px;
            }
        `,
    },
    itemCountGridItem: {
        width: 12,
        css: css`
            border-bottom: 1px solid ${color("common.border")};

            &&& {
                margin-bottom: 20px;
            }
        `,
    },
    itemCountText: {
        size: 14,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            padding-bottom: 20px;
            flex: 0 0 50%;
            padding-right: 5px;
        `,
    },
    totalLabelGridItem: {
        width: 4,
    },
    totalValueGridItem: {
        width: 8,
    },
    totalLabel: orderSummaryTotalStyle("label"),
    totalValue: orderSummaryTotalStyle(
        "value",
        css`
            margin-left: auto;
        `,
    ),
    itemLabel: orderSummaryValueTextStyle(
        false,
        css`
            padding-bottom: 0;
        `,
    ),
    itemValue: orderSummaryValueTextStyle(
        false,
        css`
            margin-left: auto;
            padding-bottom: 0;
        `,
    ),
    subtotalLabel: orderSummaryValueTextStyle(
        false,
        css`
            padding-bottom: 0;
        `,
    ),
    subtotalValue: orderSummaryValueTextStyle(
        true,
        css`
            margin-left: auto;
            padding-bottom: 0;
        `,
    ),
    hrBorder: {
        css: css`
            width: 100%;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    orderSummeryTitleGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-start;
            width: 100%;
        `,
    },
    hrBorderGridItem: {
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            height: 16px;
        `,
    },
    itemLabelGridItem: { width: 6 },
    itemValueGridItem: { width: 6 },
    shippingAndHandlingLabelGridItem: { width: 7 },
    shippingAndHandlingValueGridItem: { width: 5 },
    itemTotalGridItem: {
        width: 12,
        css: css`
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;

            > div {
                margin-top: 20px;
                &:first-child {
                    flex: 0 0 auto;
                    max-width: 50%;
                    min-width: 56px;
                    padding-right: 10px;
                }
                &:last-child {
                    flex: 0 1 auto;
                    text-align: right;
                    ${TypographyStyle} {
                        word-break: break-word;
                    }
                }
            }

            ${displayFlexOnPrint}
        `,
    },
};
